import React, {useEffect, useMemo, useState} from "react";
import {isDesktop, isMobile, isMobileOnly} from "react-device-detect";
import csvDownload from "json-to-csv-export";
import {
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Phone as CallIcon,
    Sms as SmsIcon,
    ViewList as TableIcon,
    ViewModule as CardIcon,
} from "@mui/icons-material";
import {useFiltered} from "@atttomyx/shared-hooks";
import {
    Cards,
    ConfirmDeleteDialog,
    FiltersAccordion,
    FloatingAddButton,
    FloatingButton
} from "@atttomyx/react-components";
import {ContactCard} from "../cards";
import {ContactFilters} from "../filters";
import {nodeService} from "../../services";
import {arrays, datetime, forms, strings, users as userUtils} from "@atttomyx/shared-utils";
import {communication} from "@atttomyx/react-utils";
import {nodeUtils} from "../../utils";
import {
    CSV_DELIMITER,
    CSV_FIELD_CREATED,
    CSV_FIELD_EMAIL,
    CSV_FIELD_FIRST_NAME,
    CSV_FIELD_LAST_NAME,
    CSV_FIELD_LEAD_SOURCE,
    CSV_FIELD_PHONE,
    CSV_FIELD_UPDATED,
    CSV_FILENAME_CONTACTS,
    CSV_HEADERS_CONTACTS,
    VIEW_CARDS,
    VIEW_TABLE
} from "../../constants";
import "./contactsPage.css";
import {formatPhone} from "../../utils/nodes";

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: "500px",
        margin: "0 auto",
    },
}));

const ContactsPage = (props) => {
    const {dimensions, snackbar, user, contacts, onDelete, onGotoEdit, onGotoAdd, filters} = props;
    const [contact, setContact] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const classes = useStyles();

    const decoratedContacts = useMemo(() => {
        return contacts.map(nodeUtils.decorateNode);
    }, [contacts]);

    const filtered = useFiltered(decoratedContacts, filters, userUtils.sortByName);

    const [pages, setPages] = useState([]);
    const [list, setList] = useState([]);

    useEffect(() => {
        const arrays = [];

        for (let i = 0; i < filtered.length; i += filters.paging.perPage) {
            const subArray = filtered.slice(i, i + filters.paging.perPage);

            if (subArray.length > 0) {
                arrays.push(subArray);
            }
        }

        setPages(arrays);
    }, [filtered, filters.paging.perPage]);

    useEffect(() => {
        if (filters.paging.page > -1 && pages.length > filters.paging.page) {
            setList(pages[filters.paging.page]);

        } else if (pages.length > 0) {
            setList(pages[0]);

        } else {
            setList([]);
        }
    }, [pages, filters.paging.page]);

    const downloadCsv = () => {
        const lines = [];

        filtered.forEach(contact => {
            arrays.addTo(lines, {
                [CSV_FIELD_FIRST_NAME]: contact.firstName,
                [CSV_FIELD_LAST_NAME]: contact.lastName,
                [CSV_FIELD_EMAIL]: contact.email,
                [CSV_FIELD_PHONE]: formatPhone(contact.phone),
                [CSV_FIELD_LEAD_SOURCE]: contact.leadSource,
                [CSV_FIELD_CREATED]: contact.created,
                [CSV_FIELD_UPDATED]: contact.updated,
            });
        });

        csvDownload({
            data: lines,
            delimiter: CSV_DELIMITER,
            headers: CSV_HEADERS_CONTACTS,
            filename: CSV_FILENAME_CONTACTS,
        });
    };

    const handleChangePerPage = (event) => {
        filters.paging.setPerPage(parseInt(event.target.value, 10));
    };

    const handleChangePage = (event, value) => {
        filters.paging.setPage(value);
    };

    const renderActions = (contact) => (
        <>
            {user.roles.admin ?
                <IconButton color="secondary" title="Delete" className="delete"
                            disabled={user.id === contact.id}
                            onClick={() => setContact(contact)}>
                    <DeleteIcon/>
                </IconButton> : null}
            {isMobileOnly ?
                <IconButton color="primary" title="Call"
                            disabled={user.id === contact.id || strings.isBlank(contact.phone)}
                            onClick={() => communication.popCall(contact.phone)}>
                    <CallIcon/>
                </IconButton> : null}
            {isMobile ?
                <IconButton color="primary" title="Text"
                            disabled={user.id === contact.id || strings.isBlank(contact.phone)}
                            onClick={() => communication.popSms(contact.phone)}>
                    <SmsIcon/>
                </IconButton> : null}
            <IconButton color="primary" title="Email"
                        disabled={user.id === contact.id}
                        onClick={() => communication.popEmail(contact.email)}>
                <EmailIcon/>
            </IconButton>
            {user.roles.admin ?
                <IconButton color="primary" title="Edit"
                            onClick={() => onGotoEdit(contact.id)}>
                    <EditIcon/>
                </IconButton> : null}
        </>
    );

    const pagination = <TablePagination
        classes={classes}
        component="div"
        count={filtered.length}
        labelRowsPerPage={dimensions.wide ? "per page" : ""}
        rowsPerPage={filters.paging.perPage}
        onRowsPerPageChange={handleChangePerPage}
        page={filters.paging.page >= pages.length ? 0 : filters.paging.page}
        onPageChange={handleChangePage}
        showFirstButton={dimensions.wide}
        showLastButton={dimensions.wide}
    />

    return <div className="contacts-page">
        {isDesktop ?
            <div className="toggle">
                <ToggleButtonGroup
                    value={forms.sanitizeValue(filters.options.view)}
                    exclusive
                    onChange={(event, view) => filters.setOptions({
                        view: view,
                    })}
                    aria-label="view mode"
                >
                    <Tooltip title="Cards">
                        <ToggleButton value={VIEW_CARDS}>
                            <CardIcon color={filters.options.view !== VIEW_TABLE ? "secondary" : undefined}/>
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Table">
                        <ToggleButton value={VIEW_TABLE}>
                            <TableIcon color={filters.options.view === VIEW_TABLE ? "secondary" : undefined}/>
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
            </div> : null}
        <div className="field">
            <FiltersAccordion
                filters={filters}
                form={ContactFilters}
                open={showFilters}
                onOpen={() => setShowFilters(true)}
                onClose={() => setShowFilters(false)}
            />
        </div>
        {filters.options.view !== VIEW_TABLE ? (
            <Cards
                items={filtered}
                paging={filters.paging}
                dimensions={dimensions}
                renderer={(contact) =>
                    <ContactCard key={contact.id} contact={contact} filters={filters}>
                        {renderActions(contact)}
                    </ContactCard>}
            />
        ) : (
            <>
                {pagination}
                <TableContainer component={Paper}>
                    <Table className="striped data-table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Lead Source</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Updated</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.map((contact) => (
                                <TableRow key={contact.id}>
                                    <TableCell>{userUtils.getFullName(contact)}</TableCell>
                                    <TableCell>{contact.email}</TableCell>
                                    <TableCell>{formatPhone(contact.phone)}</TableCell>
                                    <TableCell>{contact.leadSource}</TableCell>
                                    <TableCell>{datetime.getFullDateAndTime(contact.created)}</TableCell>
                                    <TableCell>{datetime.getFullDateAndTime(contact.updated)}</TableCell>
                                    <TableCell>{renderActions(contact)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        )}
        {user.roles.admin ?
            <FloatingButton
                className="floating-download"
                icon={<DownloadIcon/>}
                title="Download CSV"
                position={dimensions.portrait ? "higher" : undefined}
                center={false}
                onClick={downloadCsv}
                disabled={!isDesktop}
            /> : null}
        {user.roles.admin ?
            <FloatingAddButton
                className="floating-add"
                title="Add contact"
                position={dimensions.portrait ? "higher" : undefined}
                center={false}
                onClick={onGotoAdd}
            /> : null}
        {contact ? <ConfirmDeleteDialog
            snackbar={snackbar}
            title={userUtils.getFullName(contact)}
            onCancel={() => setContact(null)}
            onDelete={(contactId) => {
                setContact(null);
                onDelete(contactId);
            }}
            deleter={(success, failure) => nodeService.deleteNode(contact.id, success, failure)}
        /> : null}
    </div>
}

export default ContactsPage;
